import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TablePagination,
  Typography
} from '@mui/material';
import { useIntl } from 'react-intl';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import Icon from '@mui/material/Icon';
import BlockIcon from '@mui/icons-material/Block';
import Tooltip from '@mui/material/Tooltip';
import MDTypography from 'components/MDTypography';
import BatchMonitor from 'pages/knowledgeBase/batch/batchMonitor';
import { useGetBatchExecutionHistoryQuery } from 'services/dinaBatchApi';
import { BatchUtils } from 'pages/knowledgeBase/batch/batchUtils';
import { DATETIME_FORMAT, parseDate } from 'utils/formatTime';

/**
 * Component for displaying execution history of a DinaBatch
 *
 * @component
 * @param {Object} props - The properties object
 * @param {Object} props.batch - The batch object
 * @returns {React.Component} The Batch Execution History component
 */
function DinaBatchExecutionHistory({ batch }) {
  const { formatMessage: msg } = useIntl();
  const [expandedExecution, setExpandedExecution] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    data: executionHistoryResponse,
    isLoading,
    error
  } = useGetBatchExecutionHistoryQuery(
    {
      batchId: batch?.id,
      page: page,
      size: rowsPerPage
    },
    {
      skip: !batch?.id
    }
  );

  const handleToggleExpand = (jobExecutionId) => {
    setExpandedExecution(expandedExecution === jobExecutionId ? null : jobExecutionId);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedExecution(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedExecution(null);
  };

  if (!batch) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <MDTypography variant="body2" color="text.secondary">
          {msg({ id: 'loading.execution.history' })}
        </MDTypography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <MDTypography variant="body2" color="error">
          {msg({ id: 'error.loading.execution.history' })}
        </MDTypography>
      </Box>
    );
  }

  const executionHistory = executionHistoryResponse?.content || executionHistoryResponse || [];
  const totalElements = executionHistoryResponse?.totalElements || executionHistory.length;
  const isPaginated = executionHistoryResponse?.content !== undefined;

  if (!executionHistory || executionHistory.length === 0) {
    return (
      <Stack display="flex" direction="column" sx={{ height: '100%' }}>
        <Stack display="flex" direction="row" alignItems="center">
          <Icon color="text" fontSize="medium" sx={{ mr: '0.75rem' }}>
            history_icon
          </Icon>
          <MDTypography variant="h5">{msg({ id: 'batch.execution.history.title' })}</MDTypography>
          <Chip label="0" size="small" color="primary" variant="outlined" sx={{ ml: '0.75rem' }} />
        </Stack>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Stack spacing={1} alignItems="center">
            <BlockIcon color="disabled" sx={{ fontSize: 48 }} />
            <MDTypography variant="body2" color="text" textAlign="center">
              {msg({ id: 'batch.execution.history.empty' })}
            </MDTypography>
          </Stack>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack display="flex" direction="column" sx={{ height: '100%' }}>
      <Stack display="flex" direction="row" alignItems="center">
        <Icon color="text" fontSize="medium" sx={{ mr: '0.75rem' }}>
          history_icon
        </Icon>
        <MDTypography variant="h5">{msg({ id: 'batch.execution.history.title' })}</MDTypography>
        <Chip
          label={msg({ id: 'batch.execution.history.count' }, { count: totalElements })}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ ml: '0.75rem' }}
        />
      </Stack>

      <Divider />

      <List sx={{ width: '100%', p: 0, flex: 1, overflow: 'auto', minHeight: 0 }}>
        {executionHistory.map((execution, index) => {
          const isExpanded = expandedExecution === execution.jobExecutionId;
          const executionDate = parseDate(execution.executionDate);

          return (
            <div key={execution.jobExecutionId}>
              <ListItemButton
                onClick={() => handleToggleExpand(execution.jobExecutionId)}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon>{getStatusIcon(execution.status)}</ListItemIcon>

                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body1" fontWeight="medium">
                        {execution.jobExecutionId}
                      </Typography>
                      <Chip
                        label={msg({
                          id: 'batch.monitor.chip.' + execution.status,
                          defaultMessage: execution.status
                        })}
                        color={BatchUtils.getStatusColor(execution.status)}
                        size="small"
                        sx={{ color: 'white !important', fontWeight: 'bold' }}
                      />
                      {execution.errorCount > 0 ? (
                        <Tooltip
                          title={
                            execution.errorFiles && execution.errorFiles.length > 0 ? (
                              <div>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                  {msg({ id: 'batch.monitor.error.files.title' })}
                                </div>
                                {execution.errorFiles.map((fileName, index) => (
                                  <div key={index} style={{ fontSize: '0.875rem' }}>
                                    • {fileName}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              msg({ id: 'batch.monitor.error.no.details' })
                            )
                          }
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={msg(
                              { id: 'batch.monitor.error.count' },
                              { count: execution.errorCount }
                            )}
                            color="error"
                            size="small"
                            sx={{ color: 'white !important', fontWeight: 'bold' }}
                          />
                        </Tooltip>
                      ) : null}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {executionDate ? format(parseDate(executionDate), DATETIME_FORMAT) : '--'}
                    </Typography>
                  }
                />

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {execution.duration && (
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      {execution.duration}
                    </Typography>
                  )}
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </ListItemButton>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 7, pr: 2, pb: 2 }}>
                  <BatchMonitor
                    batch={batch}
                    jobExecutionId={execution.jobExecutionId}
                    compact={true}
                  />
                </Box>
              </Collapse>

              {index < executionHistory.length - 1 && <Divider />}
            </div>
          );
        })}
      </List>

      {isPaginated && totalElements > 0 && (
        <>
          <Divider />
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </>
      )}
    </Stack>
  );

  function getStatusIcon(status) {
    return (
      <Icon fontSize="large" color={BatchUtils.getStatusColor(status)}>
        {BatchUtils.getStatusIcon(status)}
      </Icon>
    );
  }
}

DinaBatchExecutionHistory.propTypes = {
  batch: PropTypes.shape({
    id: PropTypes.number.isRequired,
    batchType: PropTypes.object
  }).isRequired
};

export default DinaBatchExecutionHistory;
